/**
 * useTrash.js — Settings ▸ Trash: deleted jobs and invoices, restorable for 30 days.
 *
 * Every job/invoice delete is snapshotted server-side into deleted_records in the
 * same transaction as the DELETE (server/lib/trash.js). Restore re-inserts it with
 * its original id and re-links what was unlinked.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel } from '../lib/utils';

export function useTrash() {
  return useQuery({
    queryKey: queryKeys.trash.all,
    queryFn: async () => (await api.get('/trash')).map(snakeToCamel),
  });
}

export function useRestoreTrash() {
  const qc = useQueryClient();
  return useMutation({
    // Resolves to { table, recordId, jobId, label, warnings }. A 409 (id or
    // invoice number taken, parent job still in Trash) throws ApiError with
    // the server's sentence.
    mutationFn: (trashId) => api.post(`/trash/${trashId}/restore`),
    onSuccess: () => {
      // A job restore brings back its invoices, payout lines, time, expenses and
      // re-links quotes / crew earnings / legal docs / calendar rows.
      for (const key of [
        queryKeys.jobs.all,
        queryKeys.invoices.all,
        queryKeys.quotes.all,
        queryKeys.timeEntries.all,
        queryKeys.expenses.all,
        ['job-earnings-ledger'],
        queryKeys.crewEarnings.all,
        queryKeys.calendarEvents.all,
        ['gcal_status'],
        queryKeys.legalDocs.all,
        queryKeys.actionLog.all,
      ]) qc.invalidateQueries({ queryKey: key });
    },
    // Refresh the list on a refusal too — a 409 often means someone else
    // already restored it or the row has since been purged.
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.trash.all }),
  });
}
